import { debounce, isEqual } from 'lodash-es'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { isNil } from '@/common/utils'
import { getPreferences, updatePreferences } from '@/features/preferences/preferencesRemoteRepository'
import type { Preferences, SaleDaysSummaryPeriod, UpliftFormat } from '@/features/preferences/types'
import { useTelemetry } from '@/features/telemetry/useTelemetry'

const defaults: Preferences = {
	upliftFormat: 'score',
	didCloseGuideHintCard: false,
	timelineResolution: 'day',
	timelineSize: 3,
	timelineType: 'month',
	timelineViewType: 'timeline',
	isShowingTimelineEventsList: false,
	isShowingAllCalendarEvents: true,
	timelineResolutionPreset: 'daily',
	didCheckDontShowConflictsModalAgain: false,
	isShowingNotesOnly: false,
	isShowingScrapedOnly: false,
	isUsingStoreSpecificTimeZones: false,
	areDefaultFavouriteSKUsApplied: false,
	saleDaysSummaryPeriodV2: 'current'
}

const usePreferencesInnerStore = defineStore('preferencesInner', () => {
	const preferences = ref<Preferences>(defaults)
	const debounceDelay = ref(500)

	return {
		preferences,
		debounceDelay
	}
})

export const usePreferencesStore = defineStore('preferences', () => {
	const { trackEvent } = useTelemetry()

	const { preferences, debounceDelay } = storeToRefs(usePreferencesInnerStore())

	const didCloseGuideHintCard = computed(() => preferences.value.didCloseGuideHintCard)
	// Uplift used to be shown in percentage, which is now changed to score.
	// If user has % in preferences, show them Score (for backward compatibility)
	const upliftFormat = computed(() => (preferences.value.upliftFormat === '%' ? 'score' : preferences.value.upliftFormat))

	const timelineResolution = computed(() => preferences.value.timelineResolution)
	const timelineResolutionPreset = computed(() => preferences.value.timelineResolutionPreset)
	const timelineSize = computed(() => preferences.value.timelineSize)
	const timelineType = computed(() => preferences.value.timelineType)
	const timelineViewType = computed(() => preferences.value.timelineViewType)
	const isShowingTimelineEventsList = computed(() => preferences.value.isShowingTimelineEventsList)
	const isShowingAllCalendarEvents = computed(() => preferences.value.isShowingAllCalendarEvents)
	const didCheckDontShowConflictsModalAgain = computed(() => preferences.value.didCheckDontShowConflictsModalAgain)
	const isMonthlyResolutionActive = computed(() => timelineResolutionPreset.value === 'monthly')
	const isShowingNotesOnly = computed(() => preferences.value.isShowingNotesOnly)
	const isShowingScrapedOnly = computed(() => preferences.value.isShowingScrapedOnly)
	const isUsingStoreSpecificTimeZones = computed(() => preferences.value.isUsingStoreSpecificTimeZones)
	const areDefaultFavouriteSKUsApplied = computed(() => preferences.value.areDefaultFavouriteSKUsApplied)
	const saleDaysSummaryPeriod = computed(() => preferences.value.saleDaysSummaryPeriodV2)

	async function updatePreferencesInStore(newPreferences: Preferences) {
		const updated = await updatePreferences(newPreferences)

		if (updated) {
			preferences.value = {
				...preferences.value,
				...updated
			}
		}
	}

	/**
	 * Updates of preferences in key-value store are debounced and sent to USv2 not more often than
	 * once every 500ms (this can be override by `debounceDelay`). This is to avoid a potential race condition and `409 Conflict` response sent
	 * back from USv2. This mainly concerns new users as `409 Conflict` often occurs when new key-value
	 * entry is added and then immediately updated.
	 */
	const debouncedRemoteUpdater = computed(() =>
		debounce(() => {
			updatePreferencesInStore(preferences.value)
		}, debounceDelay.value)
	)

	function setPreferences(values: Partial<Preferences>, aDebounceDelay?: number) {
		debounceDelay.value = aDebounceDelay || 500

		const updated = {
			...preferences.value,
			...values
		}

		if (isEqual(updated, preferences.value)) {
			// Do not update preferences in the backend if there is no change compared to current state
			return
		}

		preferences.value = updated

		debouncedRemoteUpdater.value()
	}

	function setPreference(name: keyof Preferences, value: Preferences[typeof name], debounceDelay?: number) {
		setPreferences({ [name]: value }, debounceDelay)
	}

	function setUpliftFormat(format: UpliftFormat) {
		setPreference('upliftFormat', format, 2000)
	}

	function closeGuideHintCard() {
		setPreference('didCloseGuideHintCard', true)
	}

	function setTimelineResolution(resolution: Preferences['timelineResolution']) {
		setPreference('timelineResolution', resolution)
	}

	function setTimelineResolutionPreset(preset: Preferences['timelineResolutionPreset']) {
		trackEvent('app', 'Set timeline resolution', { preset })

		preferences.value.timelineResolutionPreset = preset

		switch (preset) {
			case 'daily':
				setPreferences({
					timelineResolution: 'day',
					timelineResolutionPreset: preset
				})

				break

			case 'monthly':
				setPreferences({
					timelineResolution: 'month',
					timelineResolutionPreset: preset
				})

				break
		}
	}

	function setTimelineSize(size: Preferences['timelineSize']) {
		setPreference('timelineSize', size)
	}

	function setTimelineType(type: Preferences['timelineType']) {
		setPreference('timelineType', type)
	}

	function setTimelineViewType(type: Preferences['timelineViewType']) {
		setPreference('timelineViewType', type)
	}

	function enableShowingTimelineEventsAsList() {
		setPreference('isShowingTimelineEventsList', true)
	}

	function disableShowingTimelineEventsAsList() {
		setPreference('isShowingTimelineEventsList', false)
	}

	function enableShowingAllCalendarEvents() {
		setPreference('isShowingAllCalendarEvents', true)
	}

	function disableShowingAllCalendarEvents() {
		setPreference('isShowingAllCalendarEvents', false)
	}

	function toggleShowingOnlyEventsWithNotes(value: boolean) {
		setPreference('isShowingNotesOnly', !!value)
	}

	function toggleShowingOnlyScrapedEvents(value: boolean) {
		setPreference('isShowingScrapedOnly', !!value)
	}

	function toggleUsingStoreSpeficicTimeZones(value: boolean) {
		setPreference('isUsingStoreSpecificTimeZones', !!value)
	}

	function setDefaultFavouriteSKUsAppliedFlag() {
		setPreference('areDefaultFavouriteSKUsApplied', true)
	}

	function setSaleDaysSummaryPeriod(value: SaleDaysSummaryPeriod) {
		setPreference('saleDaysSummaryPeriodV2', value)
	}

	function setDefaultTimelineResolution(numberOfVisibleMonths: number) {
		setPreferences({
			timelineSize: numberOfVisibleMonths,
			timelineType: 'month'
		})
	}

	function checkDontShowConflictsModalAgain() {
		setPreference('didCheckDontShowConflictsModalAgain', true)
	}

	async function fetch() {
		const loaded: Preferences = (await getPreferences()) ?? <Preferences>{}

		// Set default state of preferences
		preferences.value = Object.entries(defaults).reduce<Preferences>(
			(result, [key, defaultValue]) => ({
				...result,
				[key]: isNil(result[key as keyof Preferences]) ? defaultValue : result[key as keyof Preferences]
			}),
			loaded
		)
	}

	return {
		didCloseGuideHintCard,
		upliftFormat,
		timelineResolution,
		timelineResolutionPreset,
		isMonthlyResolutionActive,
		timelineSize,
		timelineType,
		timelineViewType,
		isShowingTimelineEventsList,
		isShowingAllCalendarEvents,
		isShowingNotesOnly,
		isShowingScrapedOnly,
		isUsingStoreSpecificTimeZones,
		didCheckDontShowConflictsModalAgain,
		areDefaultFavouriteSKUsApplied,
		saleDaysSummaryPeriod,
		setPreference,
		closeGuideHintCard,
		setUpliftFormat,
		setTimelineResolution,
		setTimelineResolutionPreset,
		setTimelineSize,
		setTimelineType,
		setTimelineViewType,
		enableShowingTimelineEventsAsList,
		disableShowingTimelineEventsAsList,
		enableShowingAllCalendarEvents,
		disableShowingAllCalendarEvents,
		setDefaultTimelineResolution,
		toggleShowingOnlyEventsWithNotes,
		toggleShowingOnlyScrapedEvents,
		toggleUsingStoreSpeficicTimeZones,
		checkDontShowConflictsModalAgain,
		setDefaultFavouriteSKUsAppliedFlag,
		setSaleDaysSummaryPeriod,
		fetch
	}
})
